import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MultiTable from "../../../Common/MUITable";
import {
  calculateCoveragePercent,
  filterRowsByCriteria,
} from "./reportCommon";
import {
  DEFAULT_VEHICLE_COVERAGE_FILTERS,
  VEHICLE_COVERAGE_DATA,
} from "./staticReportData";
import VehicleCoverageFilter from "./VehicleCoverageFilter";
import DelhiReportExport from "./DelhiReportExport";
import { GetVehicleDetailsReport } from "./ReportApi";

const buildCoverageRow = (row) => ({
  ...row,
  balance: row.balance !== undefined ? row.balance : (row.totalHaltPoints || 0) - (row.clearedPoints || 0),
  coveragePercent: row.clearedPercentage !== undefined 
    ? `${row.clearedPercentage}%` 
    : calculateCoveragePercent(row.totalHaltPoints, row.clearedPoints),
});

const buildTotalRow = (rows) => {
  const totals = rows.reduce(
    (acc, row) => ({
      totalHaltPoints: acc.totalHaltPoints + (Number(row.totalHaltPoints) || 0),
      clearedPoints: acc.clearedPoints + (Number(row.clearedPoints) || 0),
    }),
    { totalHaltPoints: 0, clearedPoints: 0 }
  );

  return {
    wardName: "Total",
    vehicleNo: "",
    isTotalRow: true,
    totalHaltPoints: totals.totalHaltPoints,
    clearedPoints: totals.clearedPoints,
    balance: totals.totalHaltPoints - totals.clearedPoints,
    coveragePercent: calculateCoveragePercent(
      totals.totalHaltPoints,
      totals.clearedPoints
    ),
  };
};

const totalCellStyle = { fontWeight: 700 };

const clearedLinkStyle = {
  cursor: "pointer",
  color: "#003366",
  textDecoration: "underline",
  fontWeight: 600,
};

const renderTotalAwareCell = (field) => (row) =>
  row.isTotalRow ? (
    <span style={totalCellStyle}>{row[field]}</span>
  ) : (
    row[field]
  );

const exportColumns = [
  { label: "Ward", field: "wardName" },
  { label: "Vehicle number", field: "vehicleNo" },
  { label: "Total No of Halt Points", field: "totalHaltPoints" },
  { label: "Cleared points", field: "clearedPoints" },
  { label: "Balance", field: "balance" },
  { label: "% (Total And Cleared)", field: "coveragePercent" },
];

const VehicleCoverageReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parentRow = location.state?.parentRow;
  const fromWardCoverage = Boolean(parentRow);

  const [filters, setFilters] = useState(DEFAULT_VEHICLE_COVERAGE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(
    DEFAULT_VEHICLE_COVERAGE_FILTERS
  );

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchVehicleNo, setSearchVehicleNo] = useState("");

  const fetchReportData = async (filterValues) => {
    try {
      setLoading(true);
      const params = {
        wardId: parentRow?.circleId || parentRow?.wardId || undefined,
        fromDateTime: filterValues?.fromDateTime || undefined,
        toDateTime: filterValues?.toDateTime || undefined,
      };
      
      const res = await GetVehicleDetailsReport(params);
      if (res?.data) {
        if (Array.isArray(res.data)) {
          setReportData(res.data);
        } else if (res.data.data) {
          setReportData(res.data.data);
        } else {
          setReportData([]);
        }
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching vehicle coverage report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(appliedFilters);
  }, [appliedFilters, parentRow]);

  const filteredRows = useMemo(() => {
    let rows = reportData.map((row) => ({
      ...row,
      vehicleNo: row.vehicleNo || row.vehicleNumber || "", 
    }));

    const searchKey = (searchVehicleNo || appliedFilters.vehicleNo || "").trim().toLowerCase();
    if (searchKey) {
      rows = rows.filter((row) =>
        String(row.vehicleNo).toLowerCase().includes(searchKey)
      );
    }

    const coverageRows = rows.map(buildCoverageRow);
    if (coverageRows.length === 0) return [];
    return [...coverageRows, buildTotalRow(coverageRows)];
  }, [reportData, searchVehicleNo, appliedFilters.vehicleNo]);

  const handleSearch = (values) => {
    setFilters(values);
    setSearchVehicleNo(values.vehicleNo || "");
    setAppliedFilters(values);
  };

  const handleReset = () => {
    setSearchVehicleNo("");
    setFilters(DEFAULT_VEHICLE_COVERAGE_FILTERS);
    setAppliedFilters(DEFAULT_VEHICLE_COVERAGE_FILTERS);
  };

  const handleClearedPointsClick = (row) => {
    if (!row.clearedPoints) return;

    if (row.isTotalRow) {
      const parentRows = filteredRows.filter((item) => !item.isTotalRow);
      navigate("/vehicle-cleared-points-report", {
        state: {
          showAllPoints: true,
          parentRows,
          coverageParent: parentRow || null,
        },
      });
      return;
    }

    navigate("/vehicle-cleared-points-report", {
      state: {
        parentRow: row,
        coverageParent: parentRow || null,
      },
    });
  };

  const handleBalancePointsClick = (row) => {
    if (!row.balance) return;

    if (row.isTotalRow) {
      const parentRows = filteredRows.filter((item) => !item.isTotalRow);
      navigate("/vehicle-balance-points-report", {
        state: {
          showAllPoints: true,
          parentRows,
          coverageParent: parentRow || null,
        },
      });
      return;
    }

    navigate("/vehicle-balance-points-report", {
      state: {
        parentRow: row,
        coverageParent: parentRow || null,
      },
    });
  };

  const tableColumns = [
    {
      label: "Ward",
      field: "wardName",
      width: "140px",
      render: renderTotalAwareCell("wardName"),
    },
    {
      label: "Vehicle number",
      field: "vehicleNo",
      width: "160px",
      render: renderTotalAwareCell("vehicleNo"),
    },
    {
      label: "Total No of Halt Points",
      field: "totalHaltPoints",
      width: "180px",
      render: renderTotalAwareCell("totalHaltPoints"),
    },
    {
      label: "Cleared points",
      field: "clearedPoints",
      width: "140px",
      render: (row) =>
        row.clearedPoints > 0 ? (
          <span
            style={{
              ...clearedLinkStyle,
              ...(row.isTotalRow ? totalCellStyle : {}),
            }}
            onClick={() => handleClearedPointsClick(row)}
          >
            {row.clearedPoints}
          </span>
        ) : row.isTotalRow ? (
          <span style={totalCellStyle}>{row.clearedPoints}</span>
        ) : (
          row.clearedPoints
        ),
    },
    {
      label: "Balance",
      field: "balance",
      width: "110px",
      render: (row) =>
        row.balance > 0 ? (
          <span
            style={{
              ...clearedLinkStyle,
              ...(row.isTotalRow ? totalCellStyle : {}),
            }}
            onClick={() => handleBalancePointsClick(row)}
          >
            {row.balance}
          </span>
        ) : row.isTotalRow ? (
          <span style={totalCellStyle}>{row.balance}</span>
        ) : (
          row.balance
        ),
    },
    {
      label: "% (Total And Cleared)",
      field: "coveragePercent",
      width: "200px",
      render: renderTotalAwareCell("coveragePercent"),
    },
  ];

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">Vehicle wise coverage report</h5>
            <p className="text-muted mb-0">
              View vehicle coverage.
              {parentRow?.wardName ? ` (${parentRow.wardName})` : ""}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {fromWardCoverage && (
              <button
                type="button"
                className="button-container"
                onClick={() => navigate("/ward-coverage-report")}
              >
                Back
              </button>
            )}
            <DelhiReportExport
              reportData={filteredRows}
              reportTitle="Vehicle wise coverage"
              columns={exportColumns}
              fileName="VehicleCoverageReport"
            />
          </div>
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <VehicleCoverageFilter
              initialValues={filters}
              onSearch={handleSearch}
              onReset={handleReset}
              onVehicleNoChange={(val) => setSearchVehicleNo(val)}
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={filteredRows}
                tableHeight={500}
                hasTotalRow
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleCoverageReport;
